import React, { useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Row,
  Col,
  Alert,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MedicineBoxOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { RootState } from "../../store";
import { registerStart, clearError } from "../../store/slices/authSlice";

const { Title, Text } = Typography;
const Register: React.FC = () => {
  const [form] = Form.useForm();
  const { loading, error, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  const handleSubmit = (values: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
  }) => {
    const { confirmPassword, ...registerData } = values;
    dispatch(registerStart(registerData));
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      <Row justify="center" style={{ width: "100%", maxWidth: 450 }}>
        <Col span={24}>
          <Card
            style={{
              borderRadius: 10,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            }}
            bodyStyle={{ padding: "40px 30px" }}
          >
            <div style={{ textAlign: "center", marginBottom: 30 }}>
              <MedicineBoxOutlined
                style={{
                  fontSize: 48,
                  color: "#667eea",
                  marginBottom: 16,
                }}
              />
              <Title level={2} style={{ margin: 0, color: "#333" }}>
                Registration
              </Title>
              <Text type="secondary">
                Public signup is disabled after the first admin account.
                Ask an administrator to create your user from Users menu.
              </Text>
            </div>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="If users already exist, this form will be rejected. Use Login instead."
            />

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
                closable
                onClose={() => dispatch(clearError())}
              />
            )}

            <Form
              form={form}
              name="register"
              onFinish={handleSubmit}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please input your full name!" },
                  { min: 2, message: "Name must be at least 2 characters!" },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Full Name"
                  autoComplete="name"
                />
              </Form.Item>

              <Form.Item
                name="email"
                rules={[
                  { required: true, message: "Please input your email!" },
                  { type: "email", message: "Please enter a valid email!" },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email Address"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please input your password!" },
                  {
                    min: 6,
                    message: "Password must be at least 6 characters!",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password!" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("The two passwords do not match!")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm Password"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{
                    height: 45,
                    fontSize: 16,
                    fontWeight: 500,
                  }}
                >
                  Create Account
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              <Text type="secondary">
                Already have an account?{" "}
                <Link to="/login" style={{ color: "#667eea" }}>
                  Sign in
                </Link>
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Register;
